import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { integrationsApi } from '@/services/api';
import { t } from '../i18n';
import { NEEDS_REAUTH_STATUS } from '@/config/integrations';

/**
 * Integration Re-auth Banner — shown when a connected integration's OAuth
 * token expired and could not be refreshed (status 'needs_reauth', flipped by
 * the token-refresh jobs). Without this the connection died silently:
 * status stayed "connected" in the UI while every sync/send failed.
 */
export const IntegrationReauthBanner: React.FC = () => {
  const [expiredNames, setExpiredNames] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const res = await integrationsApi.list();
        if (cancelled) return;
        const needing = (res.data ?? [])
          .filter((i) => i.status === NEEDS_REAUTH_STATUS)
          .map((i) => i.name || i.type || 'integration');
        setExpiredNames(needing);
      } catch { /* silent — the banner must never break the shell */ }
    };
    void check();
    return () => { cancelled = true; };
  }, []);

  if (expiredNames.length === 0) return null;

  return (
    <div className="bg-amber-50 border-b border-amber-200 text-amber-800 px-4 py-2 flex items-center justify-between text-sm">
      <span>
        {expiredNames.length === 1
          ? t('reauth.single', { name: expiredNames[0] })
          : t('reauth.multiple', {
              count: expiredNames.length,
              names: expiredNames.slice(0, 3).join(', '),
            })}
      </span>
      <Link
        to="/account?tab=integrations"
        className="bg-amber-600 text-white px-3 py-1 rounded font-medium hover:bg-amber-700 transition-colors text-xs"
      >
        {t('reauth.reconnect')}
      </Link>
    </div>
  );
};
